import { Eye, EyeOff } from 'lucide-react';

/**
 * O OLHO DA SENHA — mostrar e esconder o que se está digitando.
 *
 * Pedido do dono (26/10/2026): *"em todo local de digitar senha inclua o
 * ícone de olho seguindo a lógica visual do sistema e ícones padrões"*.
 *
 * ## Por que ele existe, e não é conforto
 *
 * A política de senha do sistema pede oito caracteres e NÃO impõe regra de
 * composição (01/10/2026, NIST SP 800-63B): o caminho que ela quer é a frase
 * longa, não `Senha@123`. Frase longa é exatamente o que mais se erra ao
 * digitar às cegas — e quem erra duas vezes escolhe uma senha curta na
 * terceira, que é o contrário do que a política busca. No campo, de luva e com
 * sol na tela, isso é pior ainda.
 *
 * ## Um botão só, e os campos o põem sozinhos
 *
 * Três telas do celular já tinham um olho, cada uma com o seu — rótulo
 * diferente, tamanho diferente, sem `aria-pressed`. Sete campos não tinham
 * nenhum. Duas implementações divergem na primeira correção; sete ausências
 * viram sete esquecimentos.
 *
 * Por isso o botão é UM, e quem o coloca são os campos compartilhados
 * (`CampoTexto` na mesa, `CampoCelular` no celular), pelo TIPO do campo. Campo
 * de senha novo nasce com o olho sem ninguém lembrar — que é a única forma de
 * "em todo local" ser verdade daqui a seis meses.
 *
 * ## O que o manual manda, e está cumprido
 *
 * `eye` e `eye-off` já são o vocabulário normativo (p.19: "Mostrar senha",
 * "Esconder senha") — não é conceito novo, é o conceito que já existia e não
 * estava sendo usado. Ícone sem rótulo sempre com dica (`title`), e o estado
 * dito em voz alta pelo `aria-pressed`, porque trocar o ícone não diz nada a
 * quem não o vê. O anel de foco fica: ele é alcançável por teclado, e quem
 * navega assim também digita senha.
 */
export function OlhoDaSenha({
  visivel,
  aoAlternar,
  contexto = 'mesa',
}: {
  readonly visivel: boolean;
  readonly aoAlternar: () => void;
  /**
   * Onde ele está. Muda só o tamanho do alvo: no celular o dedo precisa dos
   * 44 px da diretriz do aparelho; na mesa o ponteiro acerta 32.
   */
  readonly contexto?: 'mesa' | 'celular';
}) {
  const Icone = visivel ? EyeOff : Eye;
  /* A FRASE DIZ O QUE O TOQUE FAZ, e não o estado em que se está. "Esconder a
     senha" é o que vai acontecer; "Senha visível" seria um rótulo que descreve
     e não convida. */
  const rotulo = visivel ? 'Esconder a senha' : 'Mostrar a senha';
  return (
    <button
      type="button"
      aria-pressed={visivel}
      aria-label={rotulo}
      title={rotulo}
      onClick={aoAlternar}
      className={
        contexto === 'celular'
          ? '-mr-2 inline-flex size-[var(--cel-alvo-ios)] shrink-0 items-center justify-center rounded-controle text-tinta-apagada transition-colors hover:text-tinta-secundaria'
          : 'absolute inset-y-0 right-1 my-auto inline-flex size-8 items-center justify-center rounded-controle text-tinta-apagada transition-colors hover:bg-rebaixada hover:text-tinta-secundaria'
      }
    >
      <Icone
        aria-hidden="true"
        className={contexto === 'celular' ? 'size-[var(--icone-card)]' : 'size-[var(--icone-controle)]'}
      />
    </button>
  );
}
